<template>
  <el-card shadow="never" class="notice-page">
    <el-tabs v-model="activeTab" @tab-change="reload">
      <el-tab-pane v-for="tab in tabs" :key="tab.name" :name="tab.name">
        <template #label>
          <span>
            {{ tab.label }}
            <el-badge
              :value="unread(tab.type)"
              :hidden="unread(tab.type) === 0"
              :max="99"
            />
          </span>
        </template>
      </el-tab-pane>
    </el-tabs>

    <div class="notice-toolbar">
      <el-select v-model="isRead" class="notice-filter" @change="reload">
        <el-option label="全部消息" value="all" />
        <el-option label="未读消息" value="0" />
        <el-option label="已读消息" value="1" />
      </el-select>
      <div class="notice-toolbar-buttons">
        <el-button plain @click="handleReadAll">全部已读</el-button>
        <el-button plain @click="handleDeleteAll">全部删除</el-button>
      </div>
    </div>

    <div
      v-loading="loading"
      class="notice-list-shelf"
      :class="{
        'notice-list-shelf--placeholder': loading && list.length === 0,
      }"
    >
      <div
        v-for="item in list"
        :key="item.id"
        class="notice-item"
        :class="{ 'is-unread': item.isRead === '0' }"
      >
        <div class="notice-item-main">
          <div
            class="notice-content"
            v-html="item.content"
            @click="handleNoticeContentClick($event, item)"
          />
          <div class="notice-time">{{ item.createTime }}</div>
        </div>
        <div class="notice-actions">
          <el-button
            v-if="item.isRead === '0'"
            plain
            size="small"
            @click="markRead(item)"
          >
            已读
          </el-button>
          <el-button plain size="small" @click="handleDeleteOne(item)">删除</el-button>
        </div>
      </div>
      <el-empty v-if="!loading && list.length === 0" description="暂无消息" />
    </div>

    <div v-if="!loading && total > 0" class="notice-pager">
      <el-pagination
        v-model:current-page="pageNum"
        size="small"
        :total="total"
        :page-size="pageSize"
        layout="prev, pager, next"
        @current-change="loadList"
      />
    </div>
  </el-card>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import {
  getNoticePage,
  getUnreadCount,
  read,
  readAll,
  deleteNotice,
  deleteNoticeAll,
} from "@/api/notice";

/** name → NoticeTypeEnum.code */
const NOTICE_TABS = [
  { name: "comment", label: "评论", type: "post_comment" },
  { name: "like_favorite", label: "赞和收藏", type: "post_like_favorite" },
  { name: "follower", label: "粉丝", type: "user_follow" },
  { name: "system", label: "系统通知", type: "system" },
];

export default {
  name: "NoticeView",
  inject: {
    layoutRef: {
      default: () => ({}),
    },
  },
  data() {
    return {
      tabs: NOTICE_TABS,
      loading: false,
      isRead: "0",
      activeTab: "comment",
      pageNum: 1,
      pageSize: 10,
      total: 0,
      list: [],
      unreadByType: {},
      onNoticeChanged: null,
      bootstrapped: false,
    };
  },
  computed: {
    activeType() {
      const t = NOTICE_TABS.find((x) => x.name === this.activeTab);
      return t ? t.type : "";
    },
  },
  async mounted() {
    this.onNoticeChanged = () => this.refreshUnreadCount();
    window.addEventListener("notice-changed", this.onNoticeChanged);
    await this.refreshUnreadCount();
    this.activeTab = this.pickDefaultTab();
    this.bootstrapped = true;
    await this.loadList();
  },
  beforeUnmount() {
    if (this.onNoticeChanged) {
      window.removeEventListener("notice-changed", this.onNoticeChanged);
    }
  },
  methods: {
    pickDefaultTab() {
      const hit = NOTICE_TABS.find((t) => this.unread(t.type) > 0);
      return hit ? hit.name : "comment";
    },
    async handleNoticeContentClick(e, item) {
      const postLink = e.target?.closest?.("a[data-post-id]");
      if (postLink) {
        e.preventDefault();
        await this.ensureRead(item);
        this.layoutRef?.openPostDetail?.(postLink.getAttribute("data-post-id"));
        return;
      }
      const userLink = e.target?.closest?.("a[data-user-id]");
      if (userLink) {
        e.preventDefault();
        await this.ensureRead(item);
        const userId = userLink.getAttribute("data-user-id");
        if (userId) {
          const route = this.$router.resolve({ path: `/profile/${userId}` });
          window.open(route.href, "_blank");
        }
        return;
      }
      if (item?.isRead === "0") {
        await this.ensureRead(item);
      }
    },
    unread(type) {
      const n = this.unreadByType[type];
      return typeof n === "number" ? n : 0;
    },
    async refreshUnreadCount() {
      try {
        const res = await getUnreadCount();
        if (res?.data?.code === 200 && res.data.data) {
          this.unreadByType = { ...res.data.data };
        }
      } catch (e) {
        console.error("加载未读数失败:", e);
      }
    },
    reload() {
      if (!this.bootstrapped) return;
      this.pageNum = 1;
      this.loadList();
    },
    async loadList() {
      this.loading = true;
      try {
        const res = await getNoticePage(
          this.pageNum,
          this.pageSize,
          this.isRead === "all" ? null : this.isRead,
          this.activeType
        );
        if (res.data.code === 200) {
          const { records, total } = res.data.data;
          this.list = records || [];
          this.total = total || 0;
        }
      } finally {
        this.loading = false;
      }
      this.refreshUnreadCount();
    },
    async ensureRead(item) {
      if (!item || item.isRead !== "0") return;
      await this.markRead(item);
    },
    async markRead(item) {
      const res = await read(item.id);
      if (res.data.code === 200) {
        item.isRead = "1";
        await this.refreshUnreadCount();
        window.dispatchEvent(new Event("notice-changed"));
      }
    },
    async handleReadAll() {
      const res = await readAll(this.activeType);
      if (res.data.code === 200) {
        ElMessage.success("已全部标记为已读");
        await this.loadList();
        window.dispatchEvent(new Event("notice-changed"));
      }
    },
    async handleDeleteAll() {
      try {
        await ElMessageBox.confirm(
          "确定删除当前分类下的全部消息？删除后不可恢复。",
          "全部删除",
          {
            confirmButtonText: "删除",
            cancelButtonText: "取消",
            type: "warning",
          }
        );
      } catch {
        return;
      }
      try {
        const res = await deleteNoticeAll(this.activeType);
        if (res.data.code === 200) {
          ElMessage.success("已清空");
          this.reload();
          window.dispatchEvent(new Event("notice-changed"));
        } else {
          ElMessage.error(res.data.msg || "操作失败");
        }
      } catch (e) {
        ElMessage.error(e?.response?.data?.msg || "操作失败");
      }
    },
    async handleDeleteOne(item) {
      try {
        await ElMessageBox.confirm("确定删除这条消息？", "删除", {
          confirmButtonText: "删除",
          cancelButtonText: "取消",
          type: "warning",
        });
      } catch {
        return;
      }
      try {
        const res = await deleteNotice(item.id);
        if (res.data.code === 200) {
          ElMessage.success("已删除");
          await this.loadList();
          window.dispatchEvent(new Event("notice-changed"));
        } else {
          ElMessage.error(res.data.msg || "删除失败");
        }
      } catch (e) {
        ElMessage.error(e?.response?.data?.msg || "删除失败");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.notice-page {
  border: none;

  :deep(.el-card__body) {
    padding: 20px 30px 12px;
  }
}

.notice-filter {
  width: 140px;
}

.notice-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 8px 0;

  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}

.notice-toolbar-buttons {
  display: flex;
  gap: 10px;
}

.notice-list-shelf {
  position: relative;
  min-height: 0;
}

.notice-list-shelf--placeholder {
  min-height: 400px;
}

.notice-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.notice-item-main {
  flex: 1;
  min-width: 0;
}

.notice-content {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  color: var(--el-text-color-regular);

  :deep(a) {
    color: var(--el-color-primary);
    text-decoration: none;
    cursor: pointer;
  }
}

.notice-item.is-unread .notice-content {
  color: var(--el-text-color-primary);
  font-weight: 600;
}

.notice-time {
  margin-top: 6px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.notice-actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
}

.notice-pager {
  margin-top: 12px;
  display: flex;
  justify-content: center;
}

@media (max-width: 768px) {
  .notice-filter {
    flex: 1;
    min-width: 0;
    width: 100%;
  }

  .notice-toolbar-buttons {
    width: 100%;

    :deep(.el-button) {
      flex: 1;
      min-width: 0;
    }
  }

  .notice-item {
    flex-wrap: wrap;
    align-items: flex-start;
    padding: 12px 0;
    gap: 10px;
  }

  .notice-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
